import { useState } from "react";
import { Copy, Power, PowerOff, RefreshCw, Store, Unplug, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

function fmtAgo(iso) {
  if (!iso) return "nunca";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  return h < 48 ? `há ${h} h` : `há ${Math.floor(h / 24)} dias`;
}

function LojaRow({ m, busy, onDisconnect }) {
  const revogada = m.status === "revogado";
  // Sem o módulo Merchant o iFood não informa aberta/fechada, mas os pedidos chegam.
  const ok = !revogada && (m.open || m.merchant_module === false);
  const tone = revogada ? "text-red-600 dark:text-red-400" : ok ? "text-green-600 dark:text-green-400" : "text-amber-600 dark:text-amber-400";
  const bg = revogada ? "bg-red-500/10" : ok ? "bg-green-500/10" : "bg-amber-500/10";
  const Icon = revogada ? WifiOff : ok ? Power : PowerOff;
  const situacao = revogada
    ? "Acesso revogado no Portal do Parceiro — autorize o app de novo"
    : m.merchant_module === false
      ? "Conectada — recebendo pedidos do iFood"
    : m.reachable === false
      ? "Sem resposta do iFood agora"
      : m.open ? "Aberta no iFood — recebendo pedidos" : `Fechada no iFood${m.state_message ? ` · ${m.state_message}` : ""}`;
  const copiar = () => { try { navigator.clipboard.writeText(m.id); toast.success("ID copiado"); } catch { /* sem clipboard */ } };
  return (
    <div className="rounded-xl border bg-background p-4">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl shrink-0 flex items-center justify-center ${bg}`}>
          <Icon className={`w-6 h-6 ${tone}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base leading-tight truncate">{m.label || m.name || "Loja iFood"}</p>
          {m.name && m.label && m.name !== m.label && <p className="text-xs text-muted-foreground truncate">{m.name}</p>}
          <p className={`text-sm font-semibold mt-1.5 flex items-center gap-1.5 ${tone}`}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${revogada ? "bg-red-500" : ok ? "bg-green-500" : "bg-amber-500"}`} /> {situacao}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3 text-[11px] text-muted-foreground">
            <button onClick={copiar} className="inline-flex items-center gap-1 font-mono bg-muted px-2 py-1 rounded hover:text-foreground" title="Copiar o ID da loja">
              <Copy className="w-3 h-3" /> {m.id}
            </button>
            <span className="bg-muted px-2 py-1 rounded">último evento {fmtAgo(m.last_event_at)}</span>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-500/10" onClick={() => onDisconnect(m)} disabled={!!busy}>
          <Unplug className="w-3.5 h-3.5 mr-1" /> Desconectar
        </Button>
      </div>
    </div>
  );
}

/** Lojas do iFood vinculadas a esta conta + saúde do polling. */
export default function StatusLojaCard({ data, busy, onDisconnect, onReload }) {
  const [alvo, setAlvo] = useState(null);
  const polling = data?.polling || {};
  const merchants = data?.merchants || [];
  return (
    <div className="p-5 rounded-xl border bg-card space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold flex items-center gap-2"><Store className="w-4 h-4" /> Sua loja no iFood</h3>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full ${polling.ok ? "bg-green-500/10 text-green-700 dark:text-green-400" : "bg-amber-500/10 text-amber-700 dark:text-amber-400"}`} title="O Núcleo consulta o iFood a cada 30 s">
            <span className={`w-1.5 h-1.5 rounded-full ${polling.ok ? "bg-green-500 animate-pulse" : "bg-amber-500"}`} />
            {polling.ok
              ? `Escutando o iFood · há ${polling.seconds_ago ?? 0} s`
              : polling.waiting_authorization
                ? "Aguardando a primeira loja autorizar o Nucleo PDV no Portal do Parceiro"
                : (polling.error || "Escuta do iFood parada")}
          </span>
          <Button size="sm" variant="ghost" onClick={onReload} title="Atualizar"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {merchants.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma loja conectada ainda. Siga o passo a passo abaixo.</p>
      ) : (
        <div className="space-y-3">
          {merchants.map((m) => <LojaRow key={m.id} m={m} busy={busy} onDisconnect={setAlvo} />)}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Pedidos do iFood entram direto no Delivery. <b>Fechar/Abrir loja</b> no cabeçalho pausa e reabre a loja no iFood pelo mesmo
        tempo; o <b>aceite automático</b> é o do Delivery.
      </p>
      <AlertDialog open={!!alvo} onOpenChange={(o) => !o && setAlvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Unplug className="w-5 h-5 text-red-500" /> Desconectar esta loja do Núcleo?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p><b>{alvo?.label || alvo?.name || alvo?.id}</b> deixa de mandar pedidos para o Delivery a partir de agora.</p>
                <p>Pedidos que já estão em andamento continuam onde estão. A autorização no Portal do Parceiro não muda — para voltar, é só conectar de novo com o mesmo ID.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Manter conectada</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { onDisconnect(alvo.id); setAlvo(null); }}>
              Sim, desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
